import { Alert, Button, Card, Form, Space, Typography } from "antd";
import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router";
import { Grid as ZhGrid, GridItem as ZhGridItem, Input as ZhInput } from "zhihao-ui-react";

import request from "@/api";
import { DepartmentSelector, loadDepartmentSelectorTree, type DepartmentTreeResult } from "./DepartmentSelector";

export interface OrganizationDepartmentItem {
    id: number | string;
    name?: string;
    main?: number;
}

export interface OrganizationPersonFormValues {
    employeeId: string;
    name?: string;
    position?: string;
    mobile: string;
    mail?: string;
    deptItems?: OrganizationDepartmentItem[];
    correlationUser?: boolean;
}

export interface OrganizationPersonFormPageProps {
    loadDepartmentTree?: () => Promise<DepartmentTreeResult>;
    loadPerson?: (id: number | string) => Promise<Partial<OrganizationPersonFormValues>>;
    loadPersonMobile?: (id: number | string) => Promise<string>;
    loadPersonMail?: (id: number | string) => Promise<string>;
    savePerson?: (id: number | string | undefined, values: OrganizationPersonFormValues) => Promise<void>;
}

/** 复用旧 Vue `/admin/person/vo/:id` 员工详情接口。 */
export async function loadOrganizationPerson(id: number | string): Promise<Partial<OrganizationPersonFormValues>> {
    const response = await request.service.get<
        {
            person?: Partial<OrganizationPersonFormValues>;
            depts?: Array<Array<OrganizationDepartmentItem>>;
            dept?: OrganizationDepartmentItem;
        } & Partial<OrganizationPersonFormValues>
    >(`/admin/person/vo/${id}`);
    const departmentItems = (response.data.depts || [])
        .map(path => path[path.length - 1])
        .filter((department): department is OrganizationDepartmentItem => Boolean(department))
        .map(department => ({
            ...department,
            main: department.id === response.data.dept?.id ? 1 : 0
        }));
    return {
        ...(response.data.person || response.data),
        ...(departmentItems.length ? { deptItems: departmentItems } : {})
    };
}

/** 复用旧 Vue `/admin/person/mobile/:id` 手机号接口。 */
export async function loadOrganizationPersonMobile(id: number | string): Promise<string> {
    const response = await request.service.get<string>(`/admin/person/mobile/${id}`);
    return response.data;
}

/** 复用旧 Vue `/admin/person/mail/:id` 邮箱接口。 */
export async function loadOrganizationPersonMail(id: number | string): Promise<string> {
    const response = await request.service.get<string>(`/admin/person/mail/${id}`);
    return response.data;
}

/** 复用旧 Vue `/admin/person` 新增/编辑接口。 */
export async function saveOrganizationPerson(
    id: number | string | undefined,
    values: OrganizationPersonFormValues
): Promise<void> {
    if (id == null) {
        await request.service.post("/admin/person", values);
        return;
    }
    await request.service.put(`/admin/person/${id}`, { ...values, id });
}

/** 组织架构员工新增/编辑页，保留 Vue 表单的核心员工字段和接口契约。 */
export function OrganizationPersonFormPage({
    loadDepartmentTree = loadDepartmentSelectorTree,
    loadPerson: load = loadOrganizationPerson,
    loadPersonMobile: loadMobile = loadOrganizationPersonMobile,
    loadPersonMail: loadMail = loadOrganizationPersonMail,
    savePerson: save = saveOrganizationPerson
}: OrganizationPersonFormPageProps) {
    const navigate = useNavigate();
    const location = useLocation();
    const { id: routeId } = useParams<{ id: string }>();
    const [form] = Form.useForm<OrganizationPersonFormValues>();
    const [correlationUser, setCorrelationUser] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const id = new URLSearchParams(location.search).get("id") || routeId || undefined;

    useEffect(() => {
        if (!id) return;
        let disposed = false;
        void Promise.all([load(id), loadMobile(id), loadMail(id)])
            .then(([info, mobile, mail]) => {
                if (!disposed) {
                    form.setFieldsValue({ ...info, mobile, mail });
                    setCorrelationUser(info.correlationUser === true);
                }
            })
            .catch(() => {
                if (!disposed) setErrorMessage("员工信息加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [form, id, load, loadMail, loadMobile]);

    const handleFinish = async (values: OrganizationPersonFormValues) => {
        try {
            // Vue 保存接口只提交可编辑字段，部门/姓名/职务仅用于表单展示和校验。
            await save(id, {
                employeeId: values.employeeId,
                mail: values.mail || "",
                mobile: values.mobile
            });
            void navigate(-1);
        } catch {
            setErrorMessage("员工保存失败");
        }
    };

    return (
        <section className="flex h-full min-h-full w-full flex-col pb-24" aria-label="organization-person-form-page">
            <Card className="organization-person-form__card w-full">
                <Typography.Title className="mb-6 mt-0" level={4}>
                    {id ? "修改员工" : "新增员工"}
                </Typography.Title>
                {errorMessage ? <Alert className="mb-4" message={errorMessage} role="alert" type="error" /> : null}
                <Form<OrganizationPersonFormValues>
                    form={form}
                    layout="vertical"
                    onFinish={values => void handleFinish(values)}
                >
                    <Form.Item name="correlationUser" hidden>
                        <ZhInput />
                    </Form.Item>
                    <div
                        className="organization-person-form__grid"
                        style={{ gridTemplateColumns: "repeat(2, minmax(0, 1fr))" }}
                    >
                        <ZhGrid cols={2} gap={[24, 0]}>
                            <ZhGridItem>
                                <Form.Item
                                    label="工号"
                                    name="employeeId"
                                    rules={[
                                        { required: true, message: "请输入工号" },
                                        { pattern: /^\d{1,10}$/, message: "请输入1-10位数字" }
                                    ]}
                                >
                                    <ZhInput maxLength={10} placeholder="请输入工号" />
                                </Form.Item>
                            </ZhGridItem>
                            <ZhGridItem>
                                <Form.Item
                                    label="姓名"
                                    name="name"
                                    rules={[
                                        { required: true, message: "请输入姓名" },
                                        { min: 1, max: 64, message: "请输入1-64个字符" }
                                    ]}
                                >
                                    <ZhInput disabled placeholder="请输入真实姓名" />
                                </Form.Item>
                            </ZhGridItem>
                            <ZhGridItem>
                                <Form.Item
                                    label="部门"
                                    name="deptItems"
                                    rules={[{ required: true, message: "请选择部门", type: "array", min: 1 }]}
                                >
                                    <DepartmentSelector loadDepartmentTree={loadDepartmentTree} />
                                </Form.Item>
                            </ZhGridItem>
                            <ZhGridItem>
                                <Form.Item
                                    label="职务"
                                    name="position"
                                    rules={[{ max: 128, message: "请输入0-128个字符" }]}
                                >
                                    <ZhInput disabled placeholder="请输入职务信息" />
                                </Form.Item>
                            </ZhGridItem>
                            <ZhGridItem>
                                <Form.Item
                                    label="手机号"
                                    name="mobile"
                                    rules={[
                                        { required: true, message: "请输入手机号" },
                                        { pattern: /^1[3-9]\d{9}$/, message: "请输入正确的手机号" }
                                    ]}
                                >
                                    <ZhInput placeholder="请输入手机号" />
                                </Form.Item>
                            </ZhGridItem>
                            <ZhGridItem>
                                <Form.Item
                                    label="邮箱"
                                    name="mail"
                                    rules={[{ type: "email", message: "请输入正确的邮箱" }]}
                                >
                                    <ZhInput placeholder="请输入邮箱" />
                                </Form.Item>
                            </ZhGridItem>
                        </ZhGrid>
                        {correlationUser ? (
                            <Typography.Text className="inline-flex items-center gap-1" role="status">
                                <span aria-hidden="true">⚠</span>该员工名下存在后台用户
                            </Typography.Text>
                        ) : null}
                    </div>
                </Form>
            </Card>
            <Space className="operation-fixed-btn" data-testid="organization-person-form-actions">
                <Button type="primary" onClick={() => form.submit()}>
                    保存
                </Button>
                <Button onClick={() => void navigate(-1)}>取消</Button>
            </Space>
        </section>
    );
}

export default OrganizationPersonFormPage;
